// SelectedBuilder.jsx

import React, { useState } from 'react'
import { FileText, List } from 'lucide-react'
import styles from './styles'
import { withStyles } from '@material-ui/core'
import { Icon } from '@iconify/react'
import {
  aiInstructionConfirmSelection,
  removeIframesCompletely,
} from 'src/components/editor/utils'
import FreeAccountModal from '../free-account-modal'
import InteractionBuilder from '..'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import { useDispatch } from 'react-redux'
import UserType from 'src/model/user-types'
import { AI_INSTRUCTION } from 'src/reducers/course/type'
import {
  processStartAction,
  processStartEnd,
} from 'src/reducers/process/action'
import { snackbarShowAction } from 'src/reducers/snackbar/action'
import SnackbarMessage from 'src/model/snackbar'
import { track } from '@amplitude/analytics-browser'

const SelectedBuilder = props => {
  const { classes, position, editor, fontFamily, language } = props
  const [selectedOption, setSelectedOption] = useState(null)
  const [hoveredOption, setHoveredOption] = useState(null)
  const dispatch = useDispatch()
  const handleCancel = () => {
    dispatch(messageBoxHideAction())
  }
  const handleConfirmWithSelection = (selectedData, sourceText, colors) => {
    aiInstructionConfirmSelection(
      selectedData,
      sourceText,
      position,
      editor,
      colors,
      dispatch,
      fontFamily,
      language,
    )
  }
  const handleOptionSelect = option => {
    setSelectedOption(option)
    if (option === 'existing') {
      track('interactive_activity_build_type_selected', {
        type: 'Use Existing page content',
      })

      dispatch(processStartAction())

      dispatch({
        type: AI_INSTRUCTION,
        payload: {
          starterText: removeIframesCompletely(window.editor.getData()),
          language: language,
          callback: responseData => {
            try {
              dispatch(processStartEnd())

              // axios auto-parses the body when it is plain JSON; older
              // responses came as an SSE-wrapped string
              let data = responseData
              if (typeof responseData === 'string') {
                const jsonStart = responseData.indexOf('{')
                const jsonEnd = responseData.lastIndexOf('}') + 1
                const jsonString = responseData.substring(jsonStart, jsonEnd)
                data = JSON.parse(jsonString)
              }

              dispatch(messageBoxHideAction())
              dispatch(
                messageBoxShowAction(
                  MessageBoxContent({
                    type: MessageBoxType.WARNING,
                    title: `
                  <div
                    style="
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      gap: 8px;
                    "
                  >
                  <Typography
                    style="
                      color: var(--mcg-dark, #002E47);
                      font-family: Inter;
                      font-size: 16px;
                      font-style: normal;
                      font-weight: 400;
                      line-height: 150%; 
                    "
                  >
                    AI Interaction Builder
                  </Typography>
                </div>`,
                    draggable: false,
                    maxWidth: '920px',
                    maxHeight: '660px',
                    buttons: [],
                    component: (
                      <InteractionBuilder
                        data={data}
                        mode="pageContent"
                        sourceText={removeIframesCompletely(
                          window.editor.getData(),
                        )}
                        aiInstructionConfirmSelection={
                          handleConfirmWithSelection
                        }
                        position={position}
                        handleCancel={handleCancel}
                        language={language}
                      />
                    ),
                    callback: () => {
                      window.editor.execute('undo')
                    },
                  }),
                ),
              )
            } catch (error) {
              dispatch(
                snackbarShowAction(
                  new SnackbarMessage(`error_${Date.now()}`, 'error', [
                    'A technical issue has occurred. Please try again.',
                  ]),
                ),
              )
            }
          },
        },
      })
    } else {
      track('interactive_activity_build_type_selected', {
        type: 'Guide AI with my descriptions',
      })
      dispatch(messageBoxHideAction())
      dispatch(
        messageBoxShowAction(
          MessageBoxContent({
            type: MessageBoxType.WARNING,
            title: `
                  <div
                    style="
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      gap: 8px;
                    "
                  >
                  <Typography
                    style="
                      color: var(--mcg-dark, #002E47);
                      font-family: Inter;
                      font-size: 16px;
                      font-style: normal;
                      font-weight: 400;
                      line-height: 150%; 
                    "
                  >
                    AI Interaction Builder
                  </Typography>
                </div>`,
            draggable: false,
            maxWidth: '920px',
            maxHeight: '660px',
            buttons: [],
            component: (
              <InteractionBuilder
                mode="guide"
                position={position}
                aiInstructionConfirmSelection={handleConfirmWithSelection}
                sourceText={removeIframesCompletely(window.editor.getData())}
                handleCancel={handleCancel}
                language={language}
              />
            ),
            callback: () => {
              window.editor.execute('undo')
            },
          }),
        ),
      )
    }
  }

  // Helper function to get conditional classes
  const getOptionClasses = optionType => {
    let baseClass = classes.optionCard
    if (selectedOption === optionType) {
      baseClass += ` ${classes.optionCardSelected}`
    } else if (hoveredOption === optionType) {
      baseClass += ` ${classes.optionCardHovered}`
    }
    return baseClass
  }

  return (
    <div className={classes.container}>
      <div>
        {/* Main content area */}
        <div className={classes.mainContent}>
          {/* Header */}
          <div className={classes.header}>
            <span className={classes.headerTitle}>
              How should AI build your Interaction?
            </span>
          </div>

          {/* Options */}
          <div className={classes.optionsGrid}>
            {/* Option 1: Use existing page content */}
            <div
              className={classes.optionWrapper}
              onClick={() => handleOptionSelect('existing')}
              onMouseEnter={() => setHoveredOption('existing')}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <div className={getOptionClasses('existing')}>
                <div className={classes.iconWrapper}>
                  <Icon
                    width="32"
                    height="32"
                    icon="fluent:document-one-page-sparkle-24-regular"
                    style={{ color: '#50A7FF' }}
                  />
                </div>
                <div className={classes.textWrapper}>
                  <span className={classes.optionTitle}>
                    Use existing page content
                  </span>
                </div>
              </div>
            </div>

            {/* Option 2: Guide AI with descriptions */}
            <div
              className={classes.optionWrapper}
              onClick={() => handleOptionSelect('guide')}
              onMouseEnter={() => setHoveredOption('guide')}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <div className={getOptionClasses('guide')}>
                <div className={classes.iconWrapper}>
                  <Icon
                    width="32"
                    height="32"
                    icon="mingcute:content-ai-fill"
                    style={{ color: '#FFCC33' }}
                  />
                </div>
                <div className={classes.textWrapper}>
                  <span className={classes.optionTitle}>
                    Guide AI with my descriptions
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles, { withTheme: true })(SelectedBuilder)
